// Phase 6.B-3 — 설정 좌측 SectionNav.
// Port of design/v3/SectionNav.jsx:16-119 (사이드바) + :170-193 (SETTINGS_SECTIONS).
// children 트리는 본 phase 미사용 (사용자 결정 #6 — 매장분류 children 노출 X).
//
// Active 판정: NavLink 가 아니라 명시적 prop (`current`) — 라우트 path 매핑은
// 부모 (`SettingsLayout`) 에서 path -> id 변환 후 전달.

import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../primitives';

export interface SectionNavItem {
  id: string;
  label: string;
  icon?: string;
  sub?: string;
  disabled?: boolean;
  badge?: string;
}

export interface SettingsSectionNavProps {
  items: SectionNavItem[];
  current: string;
  onSelect: (id: string) => void;
  title?: string;
  style?: CSSProperties;
}

export function SettingsSectionNav({
  items,
  current,
  onSelect,
  title = '설정',
  style,
}: SettingsSectionNavProps) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-weak)',
        borderRadius: 'var(--radius-lg)',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        // Phase 6.C-5 — minHeight 가 모바일에서 부담스러우므로 fit-content 우선,
        // style override 가 있으면 그 값 사용. 데스크톱은 fit-content 가 자연스러움.
        height: 'fit-content',
        ...style,
      }}
    >
      <div
        style={{
          padding: '6px 8px 10px',
          borderBottom: '1px solid var(--border-weak)',
          marginBottom: 8,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-wide)',
        }}
      >
        {title}
      </div>
      {items.map((it) => (
        <SectionRow
          key={it.id}
          item={it}
          active={current === it.id}
          onClick={() => !it.disabled && onSelect(it.id)}
        />
      ))}
    </div>
  );
}

function SectionRow({
  item,
  active,
  onClick,
}: {
  item: SectionNavItem;
  active: boolean;
  onClick: () => void;
}) {
  const trailing: ReactNode = item.badge ? (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--fg-3)',
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border-weak)',
        padding: '1px 6px',
        borderRadius: 'var(--radius-full)',
      }}
    >
      {item.badge}
    </span>
  ) : null;

  return (
    <div
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!active && !item.disabled) e.currentTarget.style.background = 'var(--bg-subtle)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        marginBottom: 4,
        borderRadius: 'var(--radius-sm)',
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        background: active ? 'var(--primary-bg)' : 'transparent',
        border: active ? '1px solid var(--primary-border)' : '1px solid transparent',
        opacity: item.disabled ? 0.55 : 1,
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
    >
      <div
        style={{
          width: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.icon && (
          <Icon
            name={item.icon}
            size={14}
            color={active ? 'var(--primary-strong)' : 'var(--fg-3)'}
          />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: active ? 'var(--primary-strong)' : 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.label}
        </div>
        {item.sub && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.sub}
          </div>
        )}
      </div>
      <div>{trailing}</div>
    </div>
  );
}
